// Meteorologi (MET / api.met.no) — ekte værdata via hubbens /met-proxy.
// Docked GNÅ-panel: nåværende forhold + neste timer for et punkt (kartsenter
// eller valgt koordinat). Ren DOM. Data hentes server-side (riktig User-Agent);
// klienten kaller bare /met/forecast?lat&lon.
export interface ForecastPoint {
  time: string;
  airTemperature?: number;
  windSpeed?: number;
  windFromDirection?: number;
  relativeHumidity?: number;
  airPressure?: number;
  precipitationNext1h?: number;
  /** Skydekke (%, 0-100). Brukes også av lysforhold-modulen til NVG-vurdering. */
  cloudAreaFraction?: number;
  symbolNext1h?: string;
}
interface MetResponse { lat: number; lon: number; updatedAt?: string; series: ForecastPoint[] }

// ── Havnivå (Kartverket vannstand-API) — OBSERVERT, ikke astronomisk ────────
// I Indre Oslofjord domineres avviket fra normalvannstand av VÆR (stormflo:
// vindoppstuving + lufttrykk), ikke av selve tidevannet (Norges minste, ≈30
// cm) — Kartverket sier dette selv. Raden under viser derfor en MÅLT verdi
// fra hubbens `/havniva/observert`-proxy (som selv aldri faller tilbake på en
// beregnet/astronomisk verdi — se `packages/contracts/src/havniva.ts`).
//
// ⚠ STASJON FAST SATT TIL «OSL» (Oslo permanente stasjon): dette panelet er
// ikke posisjonsbundet ennå (i motsetning til værvarselet, som følger
// kartsenteret). For Indre Oslofjord er OSL den relevante målestasjonen. Å
// koble stasjonsvalget til kartsenter/nærmeste stasjon er en egen oppgave
// (se `tide_request=stationlist` i havniva.ts sin kommentar for hvordan man
// henter full stasjonsliste).
const HAVNIVA_STASJON = 'OSL';

interface HavnivaObs { tidspunktIso: string; verdiCm: number; flagg: string }
interface HavnivaResponse {
  stasjon: { navn: string; kode: string };
  referanseniva: string;
  sisteObservasjon: HavnivaObs | null;
}

export interface WeatherHooks {
  /** Punktet vær hentes for (default: kartsenter). */
  center: () => { lon: number; lat: number };
}

const COMPASS = ['N', 'NNØ', 'NØ', 'ØNØ', 'Ø', 'ØSØ', 'SØ', 'SSØ', 'S', 'SSV', 'SV', 'VSV', 'V', 'VNV', 'NV', 'NNV'];
function compass(deg?: number): string {
  if (deg == null) return '—';
  return COMPASS[Math.round((deg % 360) / 22.5) % 16];
}
function hhmm(iso: string): string {
  // Unngå tidssone-avhengighet: vis UTC-time fra ISO-strengen.
  const m = /T(\d{2}):(\d{2})/.exec(iso);
  return m ? `${m[1]}:${m[2]}` : '';
}

export class WeatherPanel {
  readonly el: HTMLElement;
  private open = false;
  private body!: HTMLElement;
  private sub!: HTMLElement;
  private havnivaRow!: HTMLElement;

  constructor(private hooks: WeatherHooks) {
    this.el = this.build();
  }

  toggle() {
    this.open = !this.open;
    this.el.classList.toggle('open', this.open);
    if (this.open) this.loadForCenter();
  }

  loadForCenter() {
    const c = this.hooks.center();
    void this.load(c.lat, c.lon);
  }

  private build(): HTMLElement {
    const p = div('report-composer weather-panel');
    const head = div('report-head');
    head.append(span('report-title', 'Meteorologi — MET'));
    const refresh = document.createElement('button');
    refresh.className = 'wx-refresh'; refresh.title = 'Oppdater for kartsenter'; refresh.textContent = '⟳';
    refresh.onclick = () => this.loadForCenter();
    const x = document.createElement('button');
    x.className = 'report-x'; x.textContent = '×'; x.onclick = () => this.toggle();
    head.append(refresh, x);
    p.append(head);
    this.sub = div('wx-sub'); this.sub.textContent = 'Værvarsel for kartsenter';
    p.append(this.sub);
    this.body = div('wx-body');
    this.body.append(span('wx-empty', 'Henter …'));
    p.append(this.body);
    this.havnivaRow = wxRow('Havnivå (målt)', 'Henter …');
    p.append(this.havnivaRow);
    const attrib = div('wx-attrib');
    attrib.textContent = 'Data: MET Norway (api.met.no) · Kartverket (vannstand), CC BY 4.0';
    p.append(attrib);
    return p;
  }

  /** Hent og vis vær for et punkt. Offentlig så kartklikk kan drive det. */
  async load(lat: number, lon: number) {
    this.body.replaceChildren(span('wx-empty', 'Henter værdata …'));
    try {
      const r = await fetch(`/met/forecast?lat=${lat.toFixed(4)}&lon=${lon.toFixed(4)}`, { credentials: 'include' });
      if (!r.ok) {
        // 404 på /met/forecast betyr nesten alltid MISRUTING, ikke manglende
        // værdata: den kjørende Caddy-konfigen mangler `handle /met/*`, så
        // requesten faller til COP-ens statiske nginx. Si det presist framfor
        // «utilgjengelig», så feilsøkingen ikke starter på feil sted.
        // Diagnose: deploy/route-smoke.sh
        this.body.replaceChildren(span('wx-empty',
          r.status === 401 ? 'Krever innlogget tilkobling til SKADE-hub.'
          : r.status === 404 ? 'Vær-ruten nås ikke (404) — hubbens /met-rute er ikke aktiv. Kjør deploy/route-smoke.sh.'
          : `Vær utilgjengelig (${r.status}).`));
        return;
      }
      const data = (await r.json()) as MetResponse;
      this.render(data);
    } catch {
      this.body.replaceChildren(span('wx-empty', 'Vær utilgjengelig — ingen tilkobling.'));
    }
    void this.loadHavniva();
  }

  /**
   * Hent MÅLT havnivå for {@link HAVNIVA_STASJON} og oppdater raden.
   *
   * ⚠ VISER ALDRI EN BEREGNET (astronomisk) VERDI SOM OM DEN VAR MÅLT.
   * Hubbens `/havniva/observert` svarer 502 med en presis feilgrunn når
   * stasjonen ikke har levert data — raden viser da den grunnen, ikke et
   * gjettet tall og ikke en stille tomrad.
   */
  private async loadHavniva() {
    try {
      const r = await fetch(`/havniva/observert?stasjon=${HAVNIVA_STASJON}`, { credentials: 'include' });
      if (!r.ok) {
        const feil = (await r.json().catch(() => null)) as { error?: string } | null;
        this.havnivaRow.replaceChildren(
          span('wx-rk', 'Havnivå (målt)'),
          span('wx-rv', feil?.error ?? `Vannstand utilgjengelig (${r.status}).`),
        );
        return;
      }
      const data = (await r.json()) as HavnivaResponse;
      const obs = data.sisteObservasjon;
      this.havnivaRow.replaceChildren(
        span('wx-rk', `Havnivå (målt, ${data.stasjon.navn})`),
        span('wx-rv', obs
          ? `${(obs.verdiCm / 100).toFixed(2)} m over ${data.referanseniva} · ${hhmm(obs.tidspunktIso)}Z`
          : 'Ingen observasjon i perioden.'),
      );
    } catch {
      this.havnivaRow.replaceChildren(span('wx-rk', 'Havnivå (målt)'), span('wx-rv', 'Vannstand utilgjengelig — ingen tilkobling.'));
    }
  }

  private render(data: MetResponse) {
    this.sub.textContent = `${data.lat.toFixed(3)}, ${data.lon.toFixed(3)}`
      + (data.updatedAt ? ` · oppdatert ${hhmm(data.updatedAt)}Z` : '');
    const now = data.series[0];
    this.body.replaceChildren();
    if (!now) { this.body.append(span('wx-empty', 'Ingen data for punktet.')); return; }

    // Nåværende forhold — stort tall + nøkkeltall.
    const cur = div('wx-current');
    cur.append(span('wx-temp', now.airTemperature != null ? `${Math.round(now.airTemperature)}°` : '—°'));
    const kv = div('wx-kv');
    kv.append(
      wxRow('Vind', now.windSpeed != null ? `${now.windSpeed.toFixed(1)} m/s fra ${compass(now.windFromDirection)}` : '—'),
      wxRow('Nedbør 1t', now.precipitationNext1h != null ? `${now.precipitationNext1h.toFixed(1)} mm` : '0 mm'),
      wxRow('Fukt', now.relativeHumidity != null ? `${Math.round(now.relativeHumidity)} %` : '—'),
      wxRow('Trykk', now.airPressure != null ? `${Math.round(now.airPressure)} hPa` : '—'),
      // Skydekke er både alminnelig værinfo OG inngangsdata til NVG-vurderingen
      // i lysforhold-panelet — derfor synlig her, ikke bare internt.
      wxRow('Skydekke', now.cloudAreaFraction != null ? `${Math.round(now.cloudAreaFraction)} %` : '—'),
    );
    cur.append(kv);
    this.body.append(cur);

    // Neste timer — kompakt stripe.
    const strip = div('wx-strip');
    for (const p of data.series.slice(1, 13)) {
      const cell = div('wx-cell');
      cell.append(
        span('wx-h', hhmm(p.time)),
        span('wx-ct', p.airTemperature != null ? `${Math.round(p.airTemperature)}°` : '—'),
        span('wx-cw', p.windSpeed != null ? `${Math.round(p.windSpeed)}` : '—'),
        span('wx-cp', p.precipitationNext1h ? `${p.precipitationNext1h.toFixed(1)}` : ''),
      );
      strip.append(cell);
    }
    this.body.append(div('wx-strip-l'));
    (this.body.lastElementChild as HTMLElement).textContent = 'Neste 12 t (time · temp · vind m/s · mm)';
    this.body.append(strip);
  }
}

function wxRow(k: string, v: string): HTMLElement {
  const r = div('wx-r'); r.append(span('wx-rk', k), span('wx-rv', v)); return r;
}
function div(cls: string): HTMLElement { const e = document.createElement('div'); e.className = cls; return e; }
function span(cls: string, text: string): HTMLElement { const e = document.createElement('span'); e.className = cls; e.textContent = text; return e; }
